/**
 * Auto-prompt dialog for multi-location restaurants on first visit.
 * Only opens when: 2+ locations exist AND no location is explicitly selected.
 * Uses sessionStorage to avoid re-prompting after dismissal.
 * Opens after 400ms delay to let the menu paint first.
 */

import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui'
import { LocationPicker } from '@/components/checkout'
import { MapView, type LocationMarker } from '@/components/map/MapView'
import { useMenuData } from '@/hooks/useMenuData'
import { useSelectedLocation } from '@/hooks/useSelectedLocation'
import { useAppSelector } from '@/store/hooks'

function getSessionKey(restaurantId: string | undefined) {
  return `location-prompted-${restaurantId ?? 'unknown'}`
}

export function LocationPickerSheet() {
  const { t } = useTranslation()
  const { restaurant } = useMenuData()
  const { menuLocations } = useSelectedLocation()
  const selectedLocationId = useAppSelector((s) => s.order.selectedLocationId)
  const [isOpen, setIsOpen] = useState(false)

  const isMultiLocation = menuLocations.length >= 2
  const needsPrompt = isMultiLocation && !selectedLocationId

  useEffect(() => {
    if (!needsPrompt) {
      setIsOpen(false)
      return
    }

    const key = getSessionKey(restaurant?.id)
    if (sessionStorage.getItem(key)) return

    const timer = setTimeout(() => setIsOpen(true), 400)
    return () => clearTimeout(timer)
  }, [needsPrompt, restaurant?.id])

  const handleClose = (open: boolean) => {
    if (!open) {
      const key = getSessionKey(restaurant?.id)
      sessionStorage.setItem(key, '1')
    }
    setIsOpen(open)
  }

  const locationMarkers = useMemo<LocationMarker[]>(() =>
    menuLocations
      .filter((l): l is typeof l & { latitude: number; longitude: number } =>
        l.latitude != null && l.longitude != null
      )
      .map((l) => ({ id: l.id, lat: l.latitude, lng: l.longitude, label: l.location_name })),
    [menuLocations]
  )

  const primaryCoords = locationMarkers[0] ?? null

  if (!isMultiLocation) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg overflow-hidden p-0" hideCloseButton>
        {primaryCoords && (
          <div className="w-full aspect-[3/2] overflow-hidden">
            <MapView
              isStatic
              restaurantCoords={primaryCoords}
              locations={locationMarkers}
              className="w-full h-full"
            />
          </div>
        )}
        <div className="p-4 sm:p-6">
          <DialogTitle className="text-base font-semibold mb-3">
            {t('common:choosePickupLocation')}
          </DialogTitle>
          <LocationPicker
            locations={menuLocations}
            restaurant={restaurant}
            hideTitle
            onSelect={() => setIsOpen(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
